import type { JSX } from 'react';
import { AsyncState } from '@/components/async';
import { Skeleton } from '@/components/feedback';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { useDashboardOverviewStats } from '@/hooks/useDashboardOverviewStats';
import { DashboardStatsWidget } from '@/components/dashboard/overview/DashboardStatsWidget';
import {
  BellIcon,
  CalendarIcon,
  EnvelopeClosedIcon,
  GearIcon,
  HomeIcon,
  TimerIcon,
} from '@radix-ui/react-icons';

export function DashboardOverviewStats(): JSX.Element {
  const user = useDashboardUser();
  const statsQuery = useDashboardOverviewStats(user);

  return (
    <AsyncState
      data={statsQuery.data ?? null}
      isLoading={statsQuery.isLoading || statsQuery.isFetching}
      error={statsQuery.error}
      skeleton={overviewStatsSkeleton}
    >
      {(stats) => {
        const numberFormatter = new Intl.NumberFormat();
        const formatNumber = (value: number) => numberFormatter.format(value);

        const siteSettingsUpdatedAt = formatIsoDate(stats.siteSettings.updatedAt);

        return (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <DashboardStatsWidget
              label="Properties"
              value={formatNumber(stats.properties.total)}
              detail={`With vacancy ${formatNumber(stats.properties.withVacancy)}`}
              to="/dashboard/properties"
              icon={HomeIcon}
            />
            <DashboardStatsWidget
              label="Leads with tours"
              value={formatNumber(stats.showings.scheduledLeads)}
              detail={[
                `Scheduled tours ${formatNumber(stats.showings.scheduledShowings)}`,
                `Total leads ${formatNumber(stats.leads.total)}`,
              ].join(' · ')}
              to="/dashboard/leads"
              icon={EnvelopeClosedIcon}
            />
            <DashboardStatsWidget
              label="Showing requests"
              value={formatNumber(stats.showings.requested)}
              detail="Pending showing approvals."
              to="/dashboard/showings/requests"
              icon={CalendarIcon}
            />
            <DashboardStatsWidget
              label="Tour slots today"
              value={formatNumber(stats.showingAvailability.availableSlots)}
              detail={`Booked ${formatNumber(stats.showingAvailability.bookedSlots)}`}
              to="/dashboard/showings/availability"
              icon={TimerIcon}
            />
            <DashboardStatsWidget
              label="Availability alerts"
              value={formatNumber(stats.availabilityAlerts.subscribersActive)}
              detail={`Pending emails ${formatNumber(stats.availabilityAlerts.pendingEmails)}`}
              to="/dashboard/availability-alerts"
              icon={BellIcon}
            />
            <DashboardStatsWidget
              label="Site settings"
              value={siteSettingsUpdatedAt ?? 'Not set'}
              detail="Update core settings; use Page Settings for page copy."
              to="/dashboard/site-settings"
              icon={GearIcon}
            />
          </div>
        );
      }}
    </AsyncState>
  );
}

const overviewStatsSkeleton = (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-label="Loading overview stats">
    <Skeleton className="h-28 rounded-2xl" />
    <Skeleton className="h-28 rounded-2xl" />
    <Skeleton className="h-28 rounded-2xl" />
    <Skeleton className="h-28 rounded-2xl" />
    <Skeleton className="h-28 rounded-2xl" />
    <Skeleton className="h-28 rounded-2xl" />
  </div>
);

function formatIsoDate(value: string | null): string | null {
  if (value === null) {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return trimmed.length >= 10 ? trimmed.slice(0, 10) : trimmed;
}
